// Course progress tracking utilities

/**
 * Checks the completion status of various course components
 * Returns object with status for each requirement
 */
export const checkCourseCompletion = async (course) => {
  const status = {
    syllabusSegmented: false,    // Has segmented syllabus with questions
    assignmentsReady: false,     // Has assignments that can be assigned
    answerSheetsAvailable: false, // Has answer sheet documents
    vocabListsReady: false       // Has downloadable vocab word lists
  };

  try {
    // Check if syllabus exists and is properly segmented
    const syllabusResponse = await fetch(`/syllabi/${course.file}?_=${Date.now()}`);
    if (syllabusResponse.ok) {
      const syllabusContent = await syllabusResponse.text();
      
      // Check if syllabus is segmented (has weekly structure)
      const hasWeeklyStructure = /### Week \d+:/g.test(syllabusContent);
      
      // Check if it has assessment questions
      const hasAssessments = /Assessment:|Assignments:|Evaluation:/gi.test(syllabusContent);
      
      status.syllabusSegmented = hasWeeklyStructure && hasAssessments;
    }

    // Check if coursework directory exists (assignments, answer sheets, vocab)
    const courseId = course.id;
    
    // Try multiple weeks to be more comprehensive, but require at least week1
    const weeksToCheck = ['week1', 'week2', 'week3'];
    let hasAnyAssignments = false;
    let hasAnyAnswerSheets = false;
    let hasAnyVocab = false;
    
    for (const week of weeksToCheck) {
      const courseworkChecks = [
        { type: 'assignments', url: `/coursework/${courseId}/${week}/assignments.json` },
        { type: 'answerSheets', url: `/coursework/${courseId}/${week}/answer_sheet.json` },
        { type: 'vocab', url: `/coursework/${courseId}/${week}/vocabulary.json` }
      ];

      // Check files for this week
      const weekResults = await Promise.allSettled(
        courseworkChecks.map(async (check) => {
          try {
            const response = await fetch(`${check.url}?_=${Date.now()}`);
            return { type: check.type, success: response.ok };
          } catch (err) {
            return { type: check.type, success: false };
          }
        })
      );

      // Track if we find any files
      weekResults.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.success) {
          if (result.value.type === 'assignments') hasAnyAssignments = true;
          if (result.value.type === 'answerSheets') hasAnyAnswerSheets = true;
          if (result.value.type === 'vocab') hasAnyVocab = true;
        }
      });
      
      // If we found files in week1, that's sufficient
      if (week === 'week1' && (hasAnyAssignments || hasAnyAnswerSheets || hasAnyVocab)) {
        break;
      }
    }
    
    status.assignmentsReady = hasAnyAssignments;
    status.answerSheetsAvailable = hasAnyAnswerSheets;
    status.vocabListsReady = hasAnyVocab;

  } catch (error) {
    console.error('Error checking course completion:', error);
  }

  return status;
};

/**
 * Get progress percentage (0-100)
 */
export const getCourseProgressPercentage = (status) => {
  const completed = Object.values(status).filter(Boolean).length;
  const total = Object.keys(status).length;
  return Math.round((completed / total) * 100);
};

/**
 * Get user-friendly labels for each status indicator
 */
export const getStatusLabels = () => ({
  syllabusSegmented: 'Syllabus Segmented',
  assignmentsReady: 'Assignments Ready',
  answerSheetsAvailable: 'Answer Sheets Available', 
  vocabListsReady: 'Vocab Lists Ready'
});

/**
 * Get detailed status descriptions
 */
export const getStatusDescriptions = () => ({
  syllabusSegmented: 'Course syllabus is segmented with weekly structure and assessment questions',
  assignmentsReady: 'Course has assignments that can be assigned to students',
  answerSheetsAvailable: 'Assignment answer sheets are available for download',
  vocabListsReady: 'Weekly vocabulary word lists are ready for download'
});